import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Banner, Card } from '../components/ui';

const API_URL: string = import.meta.env.VITE_API_URL ?? '/v1';
const TOKEN_KEY = 'appbuilder.accessToken'; // must match auth/store.tsx
const DEVICE_KEY = 'appbuilder.deviceId'; // must match api/client.ts (per-device build quota)

type HandoffState = 'working' | 'failed';

/**
 * PUBLIC /handoff?token=<raw> — the mobile companion's "Deploy to Production" hand-off.
 * Consumes the one-time 60s token, stores the session, and lands on /apps signed in.
 * (The mobile app has no build/download capability; this is where deployment lives.)
 */
export function HandoffPage(): JSX.Element {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';
  // Optional post-sign-in destination (e.g. the app's build/download page the phone deep-links to).
  // Only same-origin internal paths are honored — never an absolute or protocol-relative URL —
  // so this can't be turned into an open redirect.
  const nextRaw = searchParams.get('next') ?? '';
  const next = nextRaw.startsWith('/') && !nextRaw.startsWith('//') ? nextRaw : '/apps';
  const [state, setState] = useState<HandoffState>('working');
  const [message, setMessage] = useState<string>('');
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;

    if (!token) {
      setState('failed');
      setMessage('This link is missing its sign-in token.');
      return;
    }

    fetch(`${API_URL}/auth/web-handoff/consume`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token }),
    })
      .then(async (r) => {
        const body = (await r.json().catch(() => null)) as
          | { tokens?: { accessToken?: string }; deviceId?: string | null; error?: { message?: string } }
          | null;
        if (!r.ok || !body?.tokens?.accessToken) {
          throw new Error(body?.error?.message ?? 'Sign-in failed');
        }
        localStorage.setItem(TOKEN_KEY, body.tokens.accessToken);
        // Remember the initiating phone's device id so build triggers can forward it as
        // X-Device-Id — the per-device daily build quota keys on it (not this browser).
        if (body.deviceId) localStorage.setItem(DEVICE_KEY, body.deviceId);
        else localStorage.removeItem(DEVICE_KEY);
        // Full reload so AuthProvider boots with the fresh token; land on the requested page.
        window.location.replace(next);
      })
      .catch((e: unknown) => {
        setState('failed');
        setMessage(e instanceof Error ? e.message : 'Sign-in failed');
      });
  }, [token, next]);

  return (
    <div className="auth-screen">
      <div className="auth-box">
        <h1 className="auth-title">App Builder</h1>
        <Card title="Continuing from your phone">
          {state === 'working' && <Banner tone="info">Signing you in securely…</Banner>}
          {state === 'failed' && (
            <>
              <Banner>{message || 'This sign-in link is invalid or has expired.'} Links from the app work once and expire after a minute — tap “Deploy to Production” in the app again.</Banner>
              <p className="auth-alt">
                <Link to="/login">Sign in manually</Link>
              </p>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
